import { useAnalytics } from '@app/analytics/use-analytics';
import { useTrackedRedirectHandler } from '@app/analytics/utils';
import { ClipboardCheckIcon } from '@app/components/icons/clipboard-check';
import { ClipboardEditIcon } from '@app/components/icons/clipboard-edit';
import { EditIcon } from '@app/components/icons/edit';
import { PreviewIcon } from '@app/components/icons/preview';
import { ShareIcon } from '@app/components/icons/share';
import { TrashIcon } from '@app/components/icons/trash';
import { VerticalDotsIcon } from '@app/components/icons/vertical-dots';
import {
  FLAG_CONTENT_HUB_ENABLE_PAGE_DUPLICATION,
  FLAG_SPACES,
} from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import { CopyIcon } from '@app/routes/apps/components/copy-input/icons/copy-icon';
import pending from '@app/routes/content/assets/pending-page-screenshot.png';
import { PageSummary } from '@app/routes/content/domain';
import { buildCanvasEditorUrl } from '@app/routes/content/routes/utils';
import { selectCurrentSpaceExternalId } from '@app/routes/organization/selectors';
import { useAppDispatch, useAppSelector } from '@app/store';
import { getContentHubMediaHost } from '@app/utilities/utils';
import { Menu, Transition } from '@headlessui/react';
import { getBaseUrl } from '@hopin-team/dashboard-constants';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { Fragment, useCallback } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from 'react-hot-toast';

interface PageCardProps {
  page: PageSummary;
}

export const PageCard = ({ page }: PageCardProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { track } = useAnalytics();

  const duplicationState = useAppSelector(
    state => state.contentHub.pageInfo.pageDuplication,
  );

  const isDuplicating =
    duplicationState?.status === 'processing' &&
    duplicationState.pageExternalId === page.externalId;

  const handlePageDeleteConfirmation = useCallback(() => {
    dispatch({
      type: '[content] set page delete confirmation',
      payload: { pageExternalId: page.externalId, pageTitle: page.title },
    });
  }, [dispatch, page]);

  const handlePageDuplicate = useCallback(() => {
    track('ch_hubs_duplicate', { source: 'hubs_page' });
    dispatch({
      type: '[content] duplicate page clicked',
      payload: { pageExternalId: page.externalId },
    });
  }, [dispatch, page, track]);

  const handlePreviewNavigation = useCallback(() => {
    track('ch_hubs_preview', { source: 'hubs_page' });
    window.open(
      `${getBaseUrl()}/org/${page.organizationExternalId}/page/${page.slug}`,
      '_blank',
    );
  }, [page, track]);

  const flags = useFlags();
  const spaceExternalId = useAppSelector(selectCurrentSpaceExternalId);

  const HUB_EDIT_URL = buildCanvasEditorUrl(
    `/org/${page.organizationExternalId}/page/${page.externalId}/edit`,
    { spacesEnabled: Boolean(flags?.[FLAG_SPACES]), spaceExternalId },
  );
  const handleHubClick = useTrackedRedirectHandler(
    'ch_hubs_hub_click',
    HUB_EDIT_URL,
  );
  const handleHubEdit = useTrackedRedirectHandler('ch_hubs_edit', HUB_EDIT_URL);

  // TODO: remove FLAG_CONTENT_HUB_ENABLE_PAGE_DUPLICATION feature flag
  const isDuplicateEnabled = flags?.[FLAG_CONTENT_HUB_ENABLE_PAGE_DUPLICATION];

  return (
    <>
      <div className="flex flex-col gap-y-3 border border-solid rounded-lg border-gray-200 overflow-hidden pb-3 relative">
        {isDuplicating && (
          <div className="absolute inset-0 bg-white/95 flex items-center justify-center text-gray-700 z-10 font-medium">
            {t('content-hub.duplicating')}
          </div>
        )}
        <button
          onClick={handleHubClick}
          className="w-full flex-grow overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-400 hover:opacity-90 transition-opacity"
          aria-label={`Edit ${page.title}`}
        >
          <img
            src={
              page.previewThumbnailPath
                ? `${getContentHubMediaHost()}${page.previewThumbnailPath}`
                : pending
            }
            className="w-full h-full object-cover"
            alt={''}
          />
        </button>
        <div
          className="px-3 text-left font-medium text-sm text-gray-700 truncate"
          title={page.title}
        >
          {page.title}
        </div>
        <div className="flex items-center justify-between px-2">
          <div className="flex gap-x-3">
            <button
              title={t('content-hub.edit')}
              className="cursor-pointer outline-none focus:outline-none"
              onClick={handleHubEdit}
            >
              <EditIcon className="text-gray-750" />
            </button>
            {page.published && (
              <button
                title={t('content-hub.preview')}
                className="cursor-pointer outline-none focus:outline-none"
                onClick={handlePreviewNavigation}
              >
                <PreviewIcon className="text-gray-750" />
              </button>
            )}
            {page.published && (
              <CopyToClipboard
                text={`${getBaseUrl()}/org/${
                  page.organizationExternalId
                }/page/${page.slug}`}
                onCopy={() => {
                  toast.success('content-hub.copied');
                  track('ch_hubs_share', { source: 'hubs_page' });
                }}
              >
                <span
                  title={t('content-hub.share')}
                  className="cursor-pointer outline-none focus:outline-none"
                >
                  <ShareIcon className="text-gray-750" />
                </span>
              </CopyToClipboard>
            )}
          </div>
          <div className="flex gap-x-3 items-center">
            <div
              className={cx(
                'inline-flex items-center gap-1 rounded-md  px-1.5 text-xs leading-6 h-6',
                {
                  'bg-green-100/95 text-green-700': page.published,
                  'bg-gray-600 text-white': !page.published,
                },
              )}
            >
              {page.published ? (
                <>
                  <ClipboardCheckIcon className="size-3" />
                  <span>{t('content-hub.published')}</span>
                </>
              ) : (
                <>
                  <ClipboardEditIcon className="size-3" />
                  <span>{t('content-hub.draft')}</span>
                </>
              )}
            </div>
            <div className="relative">
              <Menu as="div" className="relative">
                <Menu.Button
                  title={t('content-hub.more-menu')}
                  className="outline-none"
                >
                  <VerticalDotsIcon className="text-black h-[0.875rem] w-[0.8125rem]" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute bottom-0 right-0 min-w-max z-50 flex flex-col py-1 bg-white border rounded-lg shadow-lg focus:outline-none">
                    {isDuplicateEnabled && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={cx(
                              'flex w-full items-center gap-1 text-left text-sm hover:bg-blue-100 p-2',
                              { 'bg-blue-100 text-blue-500': active },
                            )}
                            aria-label={t('content-hub.duplicate')}
                            onClick={handlePageDuplicate}
                            onKeyDown={e =>
                              e.key === 'Enter' && handlePageDuplicate()
                            }
                            disabled={isDuplicating}
                          >
                            <CopyIcon className="size-6" />
                            {t('content-hub.duplicate')}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          className={cx(
                            'flex w-full items-center gap-1 text-left text-sm hover:bg-blue-100 p-2',
                            { 'bg-blue-100 text-blue-500': active },
                          )}
                          aria-label={t('content-hub.delete')}
                          onClick={handlePageDeleteConfirmation}
                          onKeyDown={e =>
                            e.key === 'Enter' && handlePageDeleteConfirmation()
                          }
                        >
                          <TrashIcon />
                          {t('content-hub.delete')}
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
